import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

type TxFieldProps = {
    id: string | number;
    emptyBox?: boolean;
    sx?: object;
    width?: number;
    href?: string;
};

const TxField = ({
    id,
    emptyBox,
    sx,
    width = 100,
    href,
}: TxFieldProps) => {
    const [copied, setCopied] = useState(false);

    const rawId = typeof id === 'number' ? id.toString() : id;

    if (!rawId) {
        if (emptyBox) {
            return <Box sx={sx} />;
        }
        return null;
    }

    const shortId = `${rawId.slice(0, 3)}...${rawId.slice(-3)}`;

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        await navigator.clipboard.writeText(rawId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
    };

    const textNode = (
        <Tooltip title={rawId}>
            <Typography
                variant="body2"
                noWrap
                sx={{
                    minWidth: 0,
                    cursor: href ? 'pointer' : 'default',
                    color: href ? 'primary.main' : 'text.primary',
                    fontWeight: href ? 600 : 400,
                    '&:hover': href ? { textDecoration: 'underline' } : undefined,
                }}
            >
                {shortId}
            </Typography>
        </Tooltip>
    );

    return (
        <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: `${width}px`, ...sx }}
        >
            <Box
                sx={{
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                    mr: 0.5,
                }}
            >
                {href ? (
                    <Box
                        component="a"
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        sx={{
                            textDecoration: 'none',
                            display: 'block',
                        }}
                    >
                        {textNode}
                    </Box>
                ) : (
                    textNode
                )}
            </Box>

            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default TxField;